import React, { useMemo } from "react";
import { Badge, Box, CheckboxCard, CheckboxGroup, Grid, HStack, Icon, SimpleGrid, Stack, Text, Tooltip, createListCollection } from "@chakra-ui/react";
import { FaPray } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import { AvatarSelectField } from "components/utils/AvatarSelectField";
import { DashboardCheckboxCardControl, DashboardDeleteButton } from "components/utils/FormControls";
import { Button } from "components/ui/Button";
import AppAvatar from "components/utils/AppAvatar";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { useDialogForm } from "utils/useDialogForm";
import { getAvatarHexColor, getAvatarTint, resolveAvatarUrl } from "utils/avatar";
import { asArray, makePrefixedTempId, normalizeRosterRows, toAvatarSelectItems } from "../shared/helpers";
import {
  useAddDashboardActividadPreceptoria,
  useDashboardActividadPreceptorias,
  useDashboardActividadRosters,
  useRemoveDashboardActividadPreceptoria,
} from "features/dashboard/actividades/hooks";

const TMP_PREFIX = "pre_";

function rowKey(instructorId, socioId) {
  return `${instructorId}::${socioId}`;
}


function getSavedNormas(actividad) {
  return actividad?.normasPiedad?.items || [];
}

function buildNormasPiedadCompletionPayload(savedItems, completedIds) {
  const set = new Set((completedIds || []).map(String));
  return {
    items: savedItems.map((it) => ({
      norma: Number(it.norma),
      completada: set.has(String(it.norma)),
    })),
  };
}

function normaTone(seed) {
  const solid = getAvatarHexColor(seed);
  return { solid, bg: getAvatarTint(seed, 12) };
}


export const PosteriorFormacionCard = ({ actividad, actividadId, patch, normasCatalog }) => {
  const dialog = useDialogForm({ completedNormas: [], preceptorias: [] });
  const [localError, setLocalError] = React.useState(null);

  const preceptoriasQ = useDashboardActividadPreceptorias(actividadId, !!actividadId);
  const rostersQ = useDashboardActividadRosters(true);
  const addPreceptoria = useAddDashboardActividadPreceptoria();
  const removePreceptoria = useRemoveDashboardActividadPreceptoria();

  const savedNormas = useMemo(() => getSavedNormas(actividad), [actividad]);
  const savedNormaIds = useMemo(() => savedNormas.map((it) => String(it.norma)), [savedNormas]);

  const normaOptions = useMemo(
    () => (normasCatalog ?? []).filter((n) => savedNormaIds.includes(String(n.id))),
    [savedNormaIds, normasCatalog]
  );

  const completadasCount = useMemo(() => savedNormas.filter((it) => !!it.completada).length, [savedNormas]);
  const normasTotal = savedNormas.length;

  const preceptorias = asArray(preceptoriasQ.data);
  const instructoresPeriodo = useMemo(
    () =>
      normalizeRosterRows(rostersQ.data?.instructoresPeriodo, {
        idKeys: ["id", "instructorId"],
        nameKeys: ["nombre", "instructorNombre"],
        fallbackName: "Instructor",
      }),
    [rostersQ.data?.instructoresPeriodo]
  );
  const sociosPeriodo = useMemo(
    () =>
      normalizeRosterRows(rostersQ.data?.sociosPeriodo, {
        idKeys: ["id", "socioId"],
        nameKeys: ["nombre", "socioNombre"],
        fallbackName: "Socio",
      }),
    [rostersQ.data?.sociosPeriodo]
  );

  const instructoresItems = useMemo(
    () => toAvatarSelectItems(instructoresPeriodo, { fallbackLabel: "Instructor" }),
    [instructoresPeriodo]
  );
  const sociosItems = useMemo(
    () => toAvatarSelectItems(sociosPeriodo, { fallbackLabel: "Socio" }),
    [sociosPeriodo]
  );

  const instructoresCollection = useMemo(() => createListCollection({ items: instructoresItems }), [instructoresItems]);
  const sociosCollection = useMemo(() => createListCollection({ items: sociosItems }), [sociosItems]);
  const instructoresItemById = useMemo(
    () => new Map(instructoresItems.map((i) => [String(i.value), i])),
    [instructoresItems]
  );

  const preceptoriasByInstructor = useMemo(() => {
    const map = new Map();
    for (const row of preceptorias) {
      const key = row.instructorId;
      if (!key) continue;
      if (!map.has(key)) {
        const item = instructoresItemById.get(String(key));
        const nombre = row.instructorNombre || item?.label || "Instructor";
        map.set(key, {
          instructorId: row.instructorId,
          instructorNombre: nombre,
          avatar: resolveAvatarUrl(row) || item?.avatar,
          socios: [],
          count: 0,
        });
      }
      const entry = map.get(key);
      entry.count += 1;
      entry.socios.push(row.socioNombre || "Socio");
    }
    return Array.from(map.values()).sort((a, b) => a.instructorNombre.localeCompare(b.instructorNombre));
  }, [preceptorias, instructoresItemById]);

  const saving = patch.isPending || addPreceptoria.isPending || removePreceptoria.isPending;
  const patchError = patch.isError ? String(patch.error?.message || patch.error) : null;
  const preceptoriaError =
    (addPreceptoria.isError && String(addPreceptoria.error?.message || addPreceptoria.error)) ||
    (removePreceptoria.isError && String(removePreceptoria.error?.message || removePreceptoria.error)) ||
    null;
  const errorText = localError || patchError || preceptoriaError;

  function openDialog() {
    const completed = (actividad?.normasPiedad?.items || []).filter((it) => !!it.completada).map((it) => String(it.norma));
    dialog.openWith({
      completedNormas: completed,
      preceptorias: preceptorias.map((p) => ({
        id: rowKey(p.instructorId, p.socioId),
        instructorId: p.instructorId,
        socioId: p.socioId,
      })),
    });
    setLocalError(null);
  }

  function addPreceptoriaRow() {
    dialog.setField("preceptorias", [
      ...asArray(dialog.form.preceptorias),
      { id: makePrefixedTempId(TMP_PREFIX), instructorId: "", socioId: "" },
    ]);
  }

  function removePreceptoriaRow(rowId) {
    dialog.setField(
      "preceptorias",
      asArray(dialog.form.preceptorias).filter((r) => r.id !== rowId)
    );
  }

  function patchPreceptoriaRow(rowId, partial) {
    dialog.setField(
      "preceptorias",
      asArray(dialog.form.preceptorias).map((r) => (r.id === rowId ? { ...r, ...partial } : r))
    );
  }

  async function save() {
    setLocalError(null);

    const nextRows = asArray(dialog.form.preceptorias);
    const invalid = nextRows.find((r) => !r?.instructorId || !r?.socioId);
    if (invalid) {
      setLocalError("Completa instructor y socio en todas las preceptorias.");
      return;
    }

    const normalizedRows = nextRows.map((r) => ({
      instructorId: String(r.instructorId),
      socioId: String(r.socioId),
    }));

    const duplicate = normalizedRows.some(
      (r, idx) => normalizedRows.findIndex((x) => x.instructorId === r.instructorId && x.socioId === r.socioId) !== idx
    );
    if (duplicate) {
      setLocalError("No repitas pares instructor-socio en preceptorias.");
      return;
    }

    const prevKeys = new Set(preceptorias.map((p) => rowKey(p.instructorId, p.socioId)));
    const nextKeys = new Set(normalizedRows.map((r) => rowKey(r.instructorId, r.socioId)));

    const toDelete = preceptorias.filter((p) => !nextKeys.has(rowKey(p.instructorId, p.socioId)));
    const toCreate = normalizedRows.filter((r) => !prevKeys.has(rowKey(r.instructorId, r.socioId)));

    const normasPiedadPayload = buildNormasPiedadCompletionPayload(savedNormas, dialog.form.completedNormas);

    try {
      await patch.mutateAsync({
        actividadId,
        patch: { normasPiedad: normasPiedadPayload },
      });

      for (const row of toDelete) {
        await removePreceptoria.mutateAsync({ actividadId, instructorId: row.instructorId, socioId: row.socioId });
      }

      for (const row of toCreate) {
        await addPreceptoria.mutateAsync({ actividadId, instructorId: row.instructorId, socioId: row.socioId });
      }

      dialog.close();
    } catch (e) {
      setLocalError(String(e?.message || e));
      throw e;
    }
  }

  return (
    <>
      <DataCard title="Formacion" variant="purple" headerIcon={<FaPray size="24px" />} onEdit={openDialog} editAriaLabel="Editar formacion">
        <Box divideY="1px" divideColor="dashboard.border.muted">
          <Box pb="4">
            <HStack justify="space-between" align="center" mb="3">
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Normas de Piedad</Text>
              <Badge colorPalette={normasTotal > 0 && completadasCount === normasTotal ? "green" : "gray"} variant="subtle" size="sm">
                {normasTotal > 0 ? `${completadasCount}/${normasTotal} completadas` : "Sin normas"}
              </Badge>
            </HStack>

            {normasTotal === 0 ? (
              <Text color="dashboard.fg.muted" fontSize="sm">No hay normas asignadas (se definen en Previo).</Text>
            ) : (
              <SimpleGrid minChildWidth="150px" gap="2.5">
                {normaOptions.map((n) => {
                  const isDone = !!savedNormas.find((it) => String(it.norma) === String(n.id))?.completada;
                  const tone = normaTone(n.id || n.label);

                  return (
                    <HStack key={n.id} gap="2.5" p="3" rounded="2xl" bg={isDone ? tone.bg : "dashboard.bg.surfaceMuted"} borderWidth="1px" borderColor="dashboard.border.muted" opacity={isDone ? 1 : 0.68}>
                      <Box bg={isDone ? tone.solid : "dashboard.fg.subtle"} boxSize="9" display="grid" placeItems="center" rounded="full" flexShrink={0}>
                        <Icon fontSize="22px" color="white">
                          {n.icon}
                        </Icon>
                      </Box>
                      <Text fontSize="sm" fontWeight="800" color={isDone ? "dashboard.fg.default" : "dashboard.fg.muted"}>{n.label}</Text>
                    </HStack>
                  );
                })}
              </SimpleGrid>
            )}
          </Box>

          <Box pt="4">
            <HStack justify="space-between" mb="3">
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Preceptorias</Text>
              <Badge variant="subtle" size="sm">{preceptorias.length}</Badge>
            </HStack>

            {preceptoriasQ.isLoading ? (
              <Text color="dashboard.fg.muted" fontSize="sm">Cargando preceptorias...</Text>
            ) : preceptoriasByInstructor.length === 0 ? (
              <Text color="dashboard.fg.muted" fontSize="sm">Sin preceptorias registradas.</Text>
            ) : (
              <Grid templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }} gap="2.5" minW="0">
                {preceptoriasByInstructor.map((user) => {
                  const tone = normaTone(user.instructorId || user.instructorNombre);
                  const sociosLabel = user.socios.join(", ");
                  return (
                    <Tooltip.Root key={user.instructorId} openDelay={0}>
                      <Tooltip.Trigger asChild>
                        <Box rounded="2xl" p="3" bg={tone.bg} borderWidth="1px" borderColor="dashboard.border.muted" minW="0" cursor="default">
                          <HStack gap="2.5" minW="0">
                            <AppAvatar name={user.instructorNombre} src={user.avatar} seed={user.instructorId || user.instructorNombre} />
                            <Stack gap="0" minW="0" flex="1">
                              <Text fontWeight="800" fontSize="sm" color="dashboard.fg.default" truncate>{user.instructorNombre}</Text>
                              <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="700" truncate>{sociosLabel}</Text>
                            </Stack>
                            <Box rounded="pill" px="3" py="1" bg={tone.solid} color="white" fontSize="sm" fontWeight="900" lineHeight="1" flexShrink={0}>
                              {user.count}
                            </Box>
                          </HStack>
                        </Box>
                      </Tooltip.Trigger>
                      <Tooltip.Positioner>
                        <Tooltip.Content maxW="280px">
                          <Text fontWeight="800">{user.instructorNombre}</Text>
                          <Stack gap="0.5">
                            {user.socios.map((socio, index) => (
                              <Text key={`${user.instructorId}_${index}`}>{socio}</Text>
                            ))}
                          </Stack>
                        </Tooltip.Content>
                      </Tooltip.Positioner>
                    </Tooltip.Root>
                  );
                })}
              </Grid>
            )}
          </Box>
        </Box>
      </DataCard>

      <FormDialog
        open={dialog.open}
        onOpenChange={dialog.onOpenChange}
        title="Editar Formacion"
        errorText={errorText}
        saving={saving}
        onCancel={dialog.close}
        onSave={save}
      >
        <Box>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Normas de Piedad (completadas)</Text>

          {normaOptions.length === 0 ? (
            <Text color="dashboard.fg.muted">No hay normas asignadas (se definen en Previo).</Text>
          ) : (
            <CheckboxGroup
              name="normasPiedadCompleted"
              value={asArray(dialog.form.completedNormas)}
              onValueChange={(v) => {
                const next = Array.isArray(v) ? v : v?.value;
                dialog.setField("completedNormas", next ?? []);
              }}
            >
              <SimpleGrid minChildWidth="160px" gap="2">
                {normaOptions.map((n) => {
                  const tone = normaTone(n.id || n.label);
                  return (
                    <CheckboxCard.Root unstyled align="center" key={n.id} value={String(n.id)}>
                      <CheckboxCard.HiddenInput />
                      <DashboardCheckboxCardControl accentColor={tone.solid} selectedVariant="outlined" selectedBg={tone.bg}>
                        <CheckboxCard.Content display="flex" flexDirection="column" alignItems="center" textAlign="center" w="full">
                          <Box bg={tone.solid} boxSize="11" display="grid" placeItems="center" rounded="full" mb="3" mx="auto">
                            <Icon fontSize="2xl" color="white">
                              {n.icon}
                            </Icon>
                          </Box>
                          <CheckboxCard.Label fontWeight="800">{n.label}</CheckboxCard.Label>
                          <CheckboxCard.Description color="dashboard.fg.muted">{n.description}</CheckboxCard.Description>
                        </CheckboxCard.Content>
                      </DashboardCheckboxCardControl>
                    </CheckboxCard.Root>
                  );
                })}
              </SimpleGrid>
            </CheckboxGroup>
          )}
        </Box>

        <Box>
          <HStack justify="space-between" mb="2">
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default">Preceptorias</Text>
            <Button variant="surface" color="red" size="sm" onClick={addPreceptoriaRow}>
              <HStack gap="2">
                <FiPlus />
                <Text>Agregar</Text>
              </HStack>
            </Button>
          </HStack>

          {asArray(dialog.form.preceptorias).length === 0 ? (
            <Text color="dashboard.fg.muted">Sin preceptorias.</Text>
          ) : (
            <Stack gap="2">
              <Grid templateColumns="2fr 2fr auto" gap="3" px="2" py="2" bg="dashboard.bg.surfaceMuted" rounded="md">
                <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Instructor</Text>
                <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Socio</Text>
                <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted"></Text>
              </Grid>
              {asArray(dialog.form.preceptorias).map((row) => (
                <Grid key={row.id} templateColumns="2fr 2fr auto" gap="3" px="2" py="2" alignItems="center">
                  <AvatarSelectField
                    collection={instructoresCollection}
                    value={row.instructorId}
                    onChange={(v) => patchPreceptoriaRow(row.id, { instructorId: v })}
                    placeholder="Selecciona instructor"
                  />
                  <AvatarSelectField
                    collection={sociosCollection}
                    value={row.socioId}
                    onChange={(v) => patchPreceptoriaRow(row.id, { socioId: v })}
                    placeholder="Selecciona socio"
                  />
                  <DashboardDeleteButton aria-label="Eliminar preceptoria" onClick={() => removePreceptoriaRow(row.id)} />
                </Grid>
              ))}
            </Stack>
          )}
        </Box>
      </FormDialog>
    </>
  );
};
